import { newDateFromUTC, newDateToUTC } from '@bc-library/common'
import { sources } from '@bc-library/recruitment'
import { afterReceivingOne as candidateAfterReceivingOne } from '../resource/hooks'
import { beforeSending as beforeSendingOneResource } from '../resource/hooks'

// DATA RECEIVED FOM API (FROM UTC)

// GENERAL CASE : ARRAY OR SINGLE OBJECT
export const afterReceiving = (data: any) => {
  if (data && Array.isArray(data)) {
    data = data.map((object: any) => {
      return afterReceivingOne(object)
    })
  }
  else if (data.docs && data.count && Array.isArray(data.docs)) {
    data.docs = data.docs.map((object: any) => {
      return afterReceivingOne(object)
    })
  }
  else if (typeof data == 'object') {
    data = afterReceivingOne(data)
  }
  return data
}


// SINGLE OBJECT - includes all populateProcess logic
export const afterReceivingOne = (object: any) => {
  if (typeof object == 'object') {
    const result: any = {
      ...object,
      candidateId: object.candidateId && candidateAfterReceivingOne(object.candidateId),
      candidate: object.candidate && candidateAfterReceivingOne(object.candidate),
      nextContact: object.nextContact && newDateFromUTC(object.nextContact),
      _created: object._created && new Date(object._created),
      hiredDate: object.hiredDate ? new Date(object.hiredDate) : object.hiredDate,
      rejectedDate: object.rejectedDate ? new Date(object.rejectedDate) : object.rejectedDate,
      plannedContractStartDate: object.plannedContractStartDate && newDateFromUTC(object.plannedContractStartDate),
      aiScreening: object.aiScreening
        ? {
            ...object.aiScreening,
            queuedAt: object.aiScreening.queuedAt ? new Date(object.aiScreening.queuedAt) : null,
            completedAt: object.aiScreening.completedAt ? new Date(object.aiScreening.completedAt) : null,
          }
        : object.aiScreening,
    }

    // Populate candidateSourcePopulated from candidateSource
    if (object.candidateSource || object.candidateSource === 0) {
      result.candidateSourcePopulated = sources.find(source => source._id === object.candidateSource) || null
    }

    // Process comments with sorting
    if (object.comments) {
      result.comments = object.comments
        .map((comment: any) => afterReceivingComment(comment))
        .sort((a: any, b: any) => b._created - a._created)
    }

    // Process notes with sorting
    if (object.notes) {
      result.notes = object.notes
        .map((note: any) => ({
          ...note,
          _created: note._created && new Date(note._created),
        }))
        .sort((a: any, b: any) => b._created - a._created)
    }

    // Process rounds with full population
    if (object.rounds) {
      result.rounds = Array.isArray(object.rounds)
        ? object.rounds.map((round: any, index: number) => afterReceivingOneRound(round, index))
        : afterReceivingOneRound(object.rounds, object.rounds.roundIndex ?? 0)
    }

    // Process logs with sorting
    if (object.logs) {
      result.logs = object.logs
        .map((log: any) => ({
          ...log,
          _created: log._created && new Date(log._created),
          meeting: log.meeting
            ? {
                ...log.meeting,
                events: log.meeting.events.map((event: any) => ({
                  ...event,
                  start: new Date(event.start),
                  end: new Date(event.end),
                })),
              }
            : log.meeting,
        }))
        .sort((a: any, b: any) => b._created.getTime() - a._created.getTime())
    }

    return result
  }
  return object
}

// Round transformation with index
export const afterReceivingOneRound = (round: any, roundIndex: number = 0) => {
  return {
    ...round,
    roundIndex,
    candidateCancelled: round.candidateCancelled ? new Date(round.candidateCancelled) : null,
    noGo: round.noGo ? new Date(round.noGo) : null,
    goToNext: round.goToNext ? new Date(round.goToNext) : null,
    proposalDate: round.proposalDate ? new Date(round.proposalDate) : null,
    proposalAcceptedDate: round.proposalAcceptedDate ? new Date(round.proposalAcceptedDate) : null,
    proposalRejectedDate: round.proposalRejectedDate ? new Date(round.proposalRejectedDate) : null,
    proposalCanceledDate: round.proposalCanceledDate ? new Date(round.proposalCanceledDate) : null,
    evaluationCompleted: round.evaluationCompleted ? new Date(round.evaluationCompleted) : null,
    interviews: round.interviews
      ? Array.isArray(round.interviews)
        ? round.interviews.map((interview: any, index: number) => afterReceivingOneInterview(interview, index))
        : afterReceivingOneInterview(round.interviews, 0)
      : round.interviews,
  }
}

// Interview transformation with index and meeting population
const afterReceivingOneRecruiter = (recruiter: any) => ({
  ...recruiter,
  evaluationCompleted: recruiter.evaluationCompleted ? new Date(recruiter.evaluationCompleted) : null,
})

export const afterReceivingOneInterview = (interview: any, interviewIndex: number = 0) => {
  const result: any = {
    ...interview,
    interviewIndex,
    // myRecruitmentProcesses unwinds recruiters to a single object; other endpoints keep an array
    recruiters: interview.recruiters
      ? Array.isArray(interview.recruiters)
        ? interview.recruiters.map(afterReceivingOneRecruiter)
        : afterReceivingOneRecruiter(interview.recruiters)
      : interview.recruiters,
  }

  // Handle candidateMeetingPopulated
  if (interview.candidateMeetingPopulated) {
    result.candidateMeetingPopulated = {
      ...interview.candidateMeetingPopulated,
      events: interview.candidateMeetingPopulated.events
        ? interview.candidateMeetingPopulated.events.map((event: any) => ({
            ...event,
            start: new Date(event.start),
            end: new Date(event.end),
            attendees: event.attendees
              ? event.attendees.map((attendee: any) => ({
                  ...attendee,
                  sentDate: attendee.sentDate && new Date(attendee.sentDate),
                  statusChangeDate: attendee.statusChangeDate && new Date(attendee.statusChangeDate),
                  proposedStart: attendee.proposedStart && new Date(attendee.proposedStart),
                  proposedEnd: attendee.proposedEnd && new Date(attendee.proposedEnd),
                }))
              : event.attendees,
          }))
        : interview.candidateMeetingPopulated.events,
    }
  }

  // Handle meeting field (from populateInterview)
  if (interview.meeting && typeof interview.meeting !== 'string') {
    result.meeting = {
      ...interview.meeting,
      events: interview.meeting.events.map((event: any) => ({
        ...event,
        start: new Date(event.start),
        end: new Date(event.end),
        attendees: event.attendees
          ? event.attendees.map((attendee: any) => ({
              ...attendee,
              sentDate: attendee.sentDate ? new Date(attendee.sentDate) : attendee.sentDate,
              statusChangeDate: attendee.statusChangeDate ? new Date(attendee.statusChangeDate) : attendee.statusChangeDate,
              proposedStart: attendee.proposedStart ? new Date(attendee.proposedStart) : attendee.proposedStart,
              proposedEnd: attendee.proposedEnd ? new Date(attendee.proposedEnd) : attendee.proposedEnd,
            }))
          : event.attendees,
      })),
    }
  }

  return result
}

// OBJECT FIELDS

export const afterReceivingComment = (comment: any) => {
  return {
    ...comment,
    _created: comment._created && new Date(comment._created),
  }
}

export const afterReceivingCommentResponse = (commentResponse:any) => {
  return {
    ...commentResponse,
    newComment: {
      ...commentResponse.newComment,
      _created: commentResponse.newComment._created && new Date(commentResponse.newComment._created),
    }
  }
}

export const afterReceivingNoteResponse = (noteResponse:any) => {
  return {
    ...noteResponse,
    newNote: {
      ...noteResponse.newNote,
      _created: noteResponse.newNote._created && new Date(noteResponse.newNote._created),
    }
  }
}

// Group interview panel (GET sharedSlot/:meetingId): timestamps only
export const afterReceivingSharedSlot = (sharedSlot: any) => ({
  ...sharedSlot,
  meeting: {
    ...sharedSlot.meeting,
    start: new Date(sharedSlot.meeting.start),
    end: new Date(sharedSlot.meeting.end),
    recruiters: sharedSlot.meeting.recruiters.map((recruiter: any) => ({
      ...recruiter,
      proposedStart: recruiter.proposedStart && new Date(recruiter.proposedStart),
      proposedEnd: recruiter.proposedEnd && new Date(recruiter.proposedEnd),
    })),
  },
  candidates: sharedSlot.candidates.map((candidate: any) => ({
    ...candidate,
    invitation: {
      ...candidate.invitation,
      sentDate: candidate.invitation.sentDate && new Date(candidate.invitation.sentDate),
    },
    recruiters: candidate.recruiters.map((recruiter: any) => ({
      ...recruiter,
      evaluationCompleted: recruiter.evaluationCompleted && new Date(recruiter.evaluationCompleted),
    })),
  })),
})

export const afterReceivingRecruitmentProcesses = (object:any) => {
  return {
    ...object,
    recruitmentProcesses: object.recruitmentProcesses.map((process:any)=>{
      return afterReceivingOne(process);
    })
  }
}



// DATA TO BE SENT TO API (TO UTC)

// GENERAL CASE : ARRAY OR SINGLE OBJECT
export const beforeSending = (data:any) => {
  if(data && Array.isArray(data)){
    data = data.map((object:any) => {
      return beforeSendingOne(object);
    })
  }
  else if(typeof data == 'object'){
    data = beforeSendingOne(data);
  }
  return data;
};

// SINGLE OBJECT
export const beforeSendingOne = (object:any) => {
  if(typeof object == 'object'){
    return {
      ...object,
      nextContact: object.nextContact && newDateToUTC(object.nextContact),
      plannedContractStartDate: object.plannedContractStartDate && newDateToUTC(object.plannedContractStartDate),
      comments: object.comments?object.comments.map((comment:any)=>{
        return beforeSendingComment(comment);
      }):object.comments,
    }
  }
  return object;
}

export const beforeSendingCandidateAndProcess = (object:any) => {
  if(typeof object == 'object'){
    return {
      ...object,
      candidate: beforeSendingOneResource(object.candidate),
      process: beforeSendingOne(object.process)
    }
  }
    }

// OBJECT FIELDS

export const beforeSendingComment = (comment:any) => {
  return {
    ...comment,
    _created: comment._created && new Date(comment._created),
  }
}

export const beforeSendingNote = (note:any) => {
  return {
    ...note,
    _created: note._created && new Date(note._created),
  }
}


